"use client";
import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { validateEmail, validateName, validatePhone } from "@/utils/validation";
import { useFocusTrap } from "@/utils/useFocusTrap";
import { SuitabilityUserDetails } from "@/type/commonTypes";

type FieldErrors = Partial<Record<keyof SuitabilityUserDetails, string>>;

const validators = {
  name: validateName,
  phone: validatePhone,
  email: validateEmail,
};

const errorMessages = {
  name: {
    required: "Name is required",
    invalid: "Enter a valid full name",
  },
  phone: {
    required: "Phone number is required",
    invalid: "Enter valid phone number",
  },
  email: {
    required: "Email is required",
    invalid: "Invalid email address",
  },
};

const FIELDS = Object.keys(validators) as (keyof SuitabilityUserDetails)[];

export default function SuitabilityDetailsForm({
  userDetails,
  setUserDetails,
  onSubmit,
}: {
  userDetails: SuitabilityUserDetails;
  setUserDetails: React.Dispatch<React.SetStateAction<SuitabilityUserDetails>>;
  onSubmit: () => void;
}) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const stepRef = useRef<HTMLDivElement>(null);

  useFocusTrap(stepRef);

  // Handle input: update value AND clear error for that field as soon as user types
  const handleInput = (field: keyof SuitabilityUserDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (field === "name") {
      value = value.replace(/\s{2,}/g, " ");
      value = value
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    }

    if (field === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }

    setUserDetails((prev) => ({ ...prev, [field]: value }));

    // Clear error immediately on typing to encourage correction
    if (errors[field]) {
      setErrors((prev) => {
        const { ...rest } = prev;
        return rest;
      });
    }
  };

  // On blur: show error only if field is non-empty AND invalid
  const handleBlur = (field: keyof SuitabilityUserDetails) => {
    const value = userDetails[field].trim();

    if (!value) {
      // No error shown on empty blur — stay chill
      setErrors((prev) => {
        const { ...rest } = prev;
        return rest;
      });
      return;
    }

    if (!validators[field](value)) {
      setErrors((prev) => ({ ...prev, [field]: errorMessages[field].invalid }));
    } else {
      setErrors((prev) => {
        const { ...rest } = prev;
        return rest;
      });
    }
  };

  // On submit: show error for empty AND invalid fields, block submission
  const handleSubmit = () => {
    const newErrors: FieldErrors = {};

    FIELDS.forEach((field) => {
      const value = userDetails[field].trim();
      if (!value) {
        newErrors[field] = errorMessages[field].required;
      } else if (!validators[field](value)) {
        newErrors[field] = errorMessages[field].invalid;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit();
    }
  };

  return (
    <motion.div
      ref={stepRef}
      className="w-full ml-auto max-w-[85%] text-left xs:px-4 py-4 rounded-xl overflow-hidden"
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <p className="text-sm text-right font-semibold text-primaryBlack mb-2.5">Enter Details</p>

      {(Object.keys(userDetails) as (keyof SuitabilityUserDetails)[]).map((field) => (
        <label key={field} className="block relative mb-5">
          <span className="absolute top-1/2 -translate-y-1/2 left-3 text-2xs xs:text-2sm leading-4 capitalize">
            {field} :
          </span>
          <input
            type={field === "phone" ? "tel" : field}
            placeholder={`Enter Your ${field.charAt(0).toUpperCase() + field.slice(1)}`}
            inputMode={field === "phone" ? "numeric" : undefined}
            maxLength={field === "phone" ? 10 : undefined}
            className={`w-full border rounded-xl pl-15 xs:pl-16.75 pr-4 py-3 text-2xs xs:text-2sm leading-4 ${errors[field] ? "border-[#D50000]" : "border-primaryBlue"}`}
            value={userDetails[field]}
            onChange={handleInput(field)}
            onBlur={() => handleBlur(field)}
          />
          {errors[field] && (
            <p className="absolute -bottom-4.5 right-0 text-xs text-right text-[#D50000]">{errors[field]}</p>
          )}
        </label>
      ))}

      <button
        onClick={handleSubmit}
        className="block w-full max-w-fit ml-auto border border-[#1A51AA80] rounded-lg text-xs text-center font-medium text-white py-2 px-7 bg-[radial-gradient(50%_50%_at_50%_50%,#1A51AA_0%,#083E95_100%)] shine-button-hover"
      >
        Show Result
      </button>
    </motion.div>
  );
}
